import { AsyncPipe, NgClass, NgStyle } from '@angular/common'
import { AfterViewInit, Component, inject, ViewChild } from '@angular/core'
import { Router, RouterLink, RouterLinkActive } from '@angular/router'

import { MenuItem } from 'primeng/api'
import { Menubar } from 'primeng/menubar'
import { Ripple } from 'primeng/ripple'
import { combineLatest, map, Observable, takeUntil } from 'rxjs'

import { Action, Resource } from '@ti/contracts'
import { Profile } from '@ti/core/auth/services'
import { Memoize } from '@ti/core/common/decorators'
import { AccessControl } from '@ti/core/common/services'
import { injectDestroy$ } from '@ti/core/common/utils'
import { AiChatOutlineIconComponent } from '@ti/core/icons/ai-support'
import { BillingIconComponent } from '@ti/core/icons/billing'
import { ChevronIconComponent } from '@ti/core/icons/chevron'
import { CustomizeIconComponent } from '@ti/core/icons/customize'
import { DataDiagnosticsIconComponent } from '@ti/core/icons/data-diagnostics'
import { GarageIconComponent } from '@ti/core/icons/garage'
import { GeneralIconComponent } from '@ti/core/icons/general'
import { UsersIconComponent } from '@ti/core/icons/users'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { LangSwitchDialogComponent } from '@ti/core/ui-kit/i18n/components'
import { AvatarComponent } from '@ti/core/ui-kit/layout/components/avatar'
import { DynamicHostComponent } from '@ti/core/ui-kit/layout/components/dynamic-host'
import { FeedbackButtonComponent } from '@ti/core/ui-kit/layout/components/feedback-button'
import { PersonalMenuComponent, UserSettingsDialogComponent } from '@ti/shared/user'

import { MaintenanceModel } from '../../../../monitoring/models/maintenance.model'

@Component({
  selector: 'app-ti-admin-menu',
  templateUrl: 'menu.component.html',
  styleUrls: ['menu.component.scss'],
  imports: [
    Menubar,
    NgClass,
    Ripple,
    NgStyle,
    DynamicHostComponent,
    RouterLink,
    RouterLinkActive,
    FeedbackButtonComponent,
    AvatarComponent,
    ChevronIconComponent,
    PersonalMenuComponent,
    LangSwitchDialogComponent,
    UserSettingsDialogComponent,
    AsyncPipe,
  ],
})
export class TIAdminMenuComponent implements AfterViewInit {
  protected readonly accessControl = inject(AccessControl)
  protected readonly maintenanceModel = inject(MaintenanceModel)
  protected readonly router = inject(Router)
  protected readonly profile = inject(Profile)
  protected readonly languageService = inject(LanguageService)
  protected readonly destroy$ = injectDestroy$()

  @ViewChild('menubarRef')
  protected readonly menubar!: Menubar

  @Memoize()
  protected get menuItems$(): Observable<MenuItem[]> {
    return combineLatest([
      this.accessControl.massCheck$([
        [Resource.User, Action.View],
        [Resource.TSPUser, Action.View],
        [Resource.TSP, Action.View],
        [Resource.Billing, Action.View],
        [Resource.AuditLog, Action.View],
        [Resource.SystemControls, Action.View],
        [Resource.SystemControls, Action.SystemCall],
      ]),
      this.languageService.massTranslate$({
        accounts: 'ti-admin.menu.accounts',
        billing: 'tsp-admin.menu.billing',
        users: 'ti-admin.menu.users',
        tspUsers: 'ti-admin.menu.tsp-users',
        monitoring: 'ti-admin.menu.monitoring',
        systemControls: 'ti-admin.menu.system-controls',
        auditLogs: 'tsp-admin.menu.audit-logs',
        aiSupport: 'support-chat.ai-support',
      }),
      this.maintenanceModel.state$.count,
    ]).pipe(
      takeUntil(this.destroy$),
      map(
        ([
          [
            userAllowed,
            tspUserAllowed,
            accountsAllowed,
            billingAllowed,
            auditLogAllowed,
            monitoringAllowed,
            systemCallAllowed,
          ],
          labels,
          count,
        ]) => {
          return [
            {
              label: labels.tspUsers,
              iconType: UsersIconComponent,
              routerLink: 'tsp-users',
              styleClass: tspUserAllowed ? '' : 'not-allowed',
            },
            {
              label: labels.accounts,
              iconType: GarageIconComponent,
              routerLink: '/accounts',
              styleClass: accountsAllowed ? '' : 'not-allowed',
            },
            {
              label: labels.billing,
              iconType: BillingIconComponent,
              routerLink: '/billing',
              styleClass: billingAllowed ? '' : 'not-allowed',
            },
            {
              label: labels.users,
              iconType: UsersIconComponent,
              routerLink: 'admin-users',
              styleClass: userAllowed ? '' : 'not-allowed',
            },
            {
              label: labels.auditLogs,
              iconType: GeneralIconComponent,
              routerLink: '/logs',
              styleClass: auditLogAllowed ? '' : 'not-allowed',
            },
            {
              label: labels.monitoring,
              iconType: DataDiagnosticsIconComponent,
              routerLink: '/monitoring',
              badge: count > 0 ? count.toString() : undefined,
              styleClass: monitoringAllowed ? '' : 'not-allowed',
            },
            ...(systemCallAllowed
              ? [
                  {
                    label: labels.systemControls,
                    iconType: CustomizeIconComponent,
                    routerLink: '/system-controls',
                  },
                ]
              : []),
            {
              label: labels.aiSupport,
              iconType: AiChatOutlineIconComponent,
              routerLink: '/ai-support',
            },
          ]
        },
      ),
    )
  }

  public ngAfterViewInit() {
    // Disable the focus handler as a hotfix
    this.menubar.onMenuFocus = () => undefined
  }
}
